

















































































































































































































































































































































































































































































import moment from 'moment'
import { Vue, Component } from 'vue-property-decorator'
import { mapGetters } from 'vuex'
import NavButton from '@/components/shared/NavButton.vue'
import { Pages } from '@/pages'
import { IAuditFieldChange, IAuditTrailEntry, IAuditTrailFilters, IAuditMetadata, IAuditRetentionPolicy, ISnackbar } from '@/model'
import { permissions } from '@/userUtils'
import { userProfiles } from '@/api/const'
import { UserData } from '@/generated/model/userData'

interface IAuditDisplayItem {
  key: string
  label: string
  value: any
  mono?: boolean
  copyable?: boolean
  max?: number
}

interface IAuditUserFilterItem {
  text: string
  value: string
}

interface IAuditTrailLocalFilters extends Omit<IAuditTrailFilters, 'user'> {
  actorUser: string
}

@Component({
  components: { NavButton },
  computed: {
    ...mapGetters('auditTrail', {
      auditTrail: 'auditTrail',
      auditTrailTotalCount: 'auditTrailTotalCount',
      auditTrailFilters: 'auditTrailFilters',
      isAuditTrailLoading: 'isAuditTrailLoading',
      isAuditTrailExporting: 'isAuditTrailExporting',
      auditRetentionPolicy: 'auditRetentionPolicy',
      isAuditRetentionPolicyLoading: 'isAuditRetentionPolicyLoading',
      isAuditRetentionPolicySaving: 'isAuditRetentionPolicySaving'
    }),
    ...mapGetters('user', ['userPermissions', 'allUserProfiles'])
  }
})
export default class AuditTrail extends Vue {
  private homeRouteLink: any = Pages.HOME.routeLink
  private auditTrail!: IAuditTrailEntry[]
  private auditTrailTotalCount!: number
  private auditTrailFilters!: IAuditTrailFilters
  private isAuditTrailLoading!: boolean
  private isAuditTrailExporting!: boolean
  private auditRetentionPolicy!: IAuditRetentionPolicy | null
  private isAuditRetentionPolicyLoading!: boolean
  private isAuditRetentionPolicySaving!: boolean
  private userPermissions!: string[]
  private allUserProfiles!: UserData[]
  private detailsDialog: boolean = false
  private selectedLog: IAuditTrailEntry | null = null
  private snackbar: ISnackbar = { show: false, color: '', text: '', timeout: 5000 }
  private userProfilesList = userProfiles
  private localFilters: IAuditTrailLocalFilters = {
    from: '',
    to: '',
    actorUser: '',
    profile: '',
    action: '',
    limit: 50,
    offset: 0
  }
  private localRetentionPolicy: IAuditRetentionPolicy = this.defaultRetentionPolicy()

  private actionTypes: string[] = [
    'LOGIN',
    'LOGIN_FAILED',
    'LOGOUT',
    'VIEW_AUDIT_TRAIL',
    'EXPORT_AUDIT_TRAIL',
    'PRUNE_AUDIT_TRAIL',
    'UPDATE_AUDIT_TRAIL_RETENTION_POLICY',
    'UPDATE_USER_LOGIN_REQUIRED',
    'UPDATE_USER_PASSWORD',
    'UPDATE_SETTINGS',
    'RESET_SETTINGS',
    'UPDATE_MACHINE_SETTINGS',
    'RESET_MACHINE_SETTINGS',
    'SET_MACHINE_SETTINGS_FIXED',
    'CREATE_PALLET_PATTERN',
    'UPDATE_PALLET_PATTERN',
    'DELETE_PALLET_PATTERN',
    'DELETE_ALL_PALLET_PATTERNS',
    'IMPORT_PALLET_PATTERNS',
    'SEND_PALLET_PATTERN_TO_ROBOT',
    'VALIDATE_PALLET_PATTERN',
    'CANCEL_PALLET_PATTERN_VALIDATION',
    'CREATE_BACKUP',
    'RESTORE_BACKUP',
    'UPDATE_BACKUP_SETTINGS',
    'INSTALL_LICENSE',
    'REMOVE_LICENSE'
  ]

  async mounted () {
    this.localFilters = this.fromAuditTrailFilters(this.auditTrailFilters)
    if (!this.canView) {
      this.showSnackBar('warning', this.localText('settings.permissionDenied'))
      if (this.$router) {
        await this.$router.replace(this.homeRouteLink)
      }
      return
    }
    await this.loadUserProfiles()
    if (this.canManageRetention) {
      await this.loadRetentionPolicy()
    }
    await this.loadLogs(this.localFilters)
  }

  get headers () {
    return [
      { text: this.localText('auditTrail.timestamp'), value: 'timestamp', sortable: false },
      { text: this.localText('auditTrail.user'), value: 'username', sortable: false },
      { text: this.localText('auditTrail.action'), value: 'actionType', sortable: false },
      { text: this.localText('auditTrail.entity'), value: 'entity', sortable: false },
      { text: this.localText('auditTrail.description'), value: 'description', sortable: false },
      { text: '', value: 'details', sortable: false, align: 'right' }
    ]
  }

  get fieldChangeHeaders () {
    return [
      { text: this.localText('auditTrail.field'), value: 'field', sortable: false },
      { text: this.localText('auditTrail.before'), value: 'beforeValue', sortable: false },
      { text: this.localText('auditTrail.after'), value: 'afterValue', sortable: false }
    ]
  }

  get canExport (): boolean {
    return this.canView && this.userPermissions.includes(permissions.EXPORTAUDITTRAIL)
  }

  get canView (): boolean {
    return this.userPermissions.includes(permissions.VIEWAUDITTRAIL)
  }

  get canManageRetention (): boolean {
    return this.canView && this.userPermissions.includes(permissions.MANAGEAUDITTRAIL)
  }

  get retentionSummary (): string {
    if (!this.localRetentionPolicy.enabled) {
      return this.localText('auditTrail.retentionDisabledSummary')
    }
    return this.localText('auditTrail.retentionEnabledSummary', [
      String(this.localRetentionPolicy.retentionDays || 0),
      String(this.localRetentionPolicy.cleanupIntervalHours || 0),
      String(this.localRetentionPolicy.cleanupBatchSize || 0)
    ])
  }

  get userFilterItems (): IAuditUserFilterItem[] {
    const usersByName: {[key: string]: IAuditUserFilterItem} = {}
    const auditTrailEntries = this.auditTrail || []
    auditTrailEntries.forEach(entry => {
      const actorUser = this.normalizedActorUser(entry)
      if (actorUser && this.isDistinctActorUser(entry)) {
        this.addUserFilterItem(usersByName, actorUser, this.userDisplayName(actorUser))
      }
    })
    if (this.localFilters.actorUser) {
      this.addUserFilterItem(usersByName, this.localFilters.actorUser, this.userDisplayName(this.localFilters.actorUser))
    }
    return Object.keys(usersByName)
      .map(key => usersByName[key])
      .sort((a, b) => a.text.localeCompare(b.text))
  }

  get showActorUserFilter (): boolean {
    if (this.localFilters.actorUser) {
      return true
    }
    const auditTrailEntries = this.auditTrail || []
    return auditTrailEntries.some(entry => this.isDistinctActorUser(entry))
  }

  get userProfileFilterItems (): IAuditUserFilterItem[] {
    const profilesByName: {[key: string]: IAuditUserFilterItem} = {}
    this.userProfilesList.forEach(profile => {
      this.addUserFilterItem(profilesByName, profile.id, profile.name)
    })
    const allProfiles = this.allUserProfiles || []
    allProfiles.forEach(profile => {
      const profileName = profile && profile.profile ? profile.profile : ''
      if (!profileName) {
        return
      }
      this.addUserFilterItem(profilesByName, profileName, this.userDisplayName(profileName))
    })
    const auditTrailEntries = this.auditTrail || []
    auditTrailEntries.forEach(entry => {
      if (entry && entry.userProfile) {
        this.addUserFilterItem(profilesByName, entry.userProfile, this.userDisplayName(entry.userProfile))
      }
    })
    if (this.localFilters.profile) {
      this.addUserFilterItem(profilesByName, this.localFilters.profile, this.userDisplayName(this.localFilters.profile))
    }
    return Object.keys(profilesByName)
      .map(key => profilesByName[key])
      .sort((a, b) => a.text.localeCompare(b.text))
  }

  get canGoPrevious (): boolean {
    return Number(this.localFilters.offset || 0) > 0
  }

  get canGoNext (): boolean {
    const offset = Number(this.localFilters.offset || 0)
    const limit = Number(this.localFilters.limit || 50)
    const auditTrailLength = this.auditTrail ? this.auditTrail.length : 0
    return offset + auditTrailLength < this.auditTrailTotalCount && auditTrailLength >= limit
  }

  get paginationSummary (): string {
    if (!this.auditTrail || this.auditTrail.length < 1 || this.auditTrailTotalCount < 1) {
      return this.localText('auditTrail.noLogs')
    }
    const start = Number(this.localFilters.offset || 0) + 1
    const end = Number(this.localFilters.offset || 0) + this.auditTrail.length
    return this.localText('auditTrail.showing', [String(start), String(end), String(this.auditTrailTotalCount)])
  }

  get selectedMetadata (): IAuditMetadata {
    if (!this.selectedLog || !this.selectedLog.metadata) {
      return {}
    }
    return this.selectedLog.metadata
  }

  get selectedFieldChanges (): IAuditFieldChange[] {
    if (!this.selectedMetadata.fieldChanges) {
      return []
    }
    return this.selectedMetadata.fieldChanges
  }

  get metadataItems (): IAuditDisplayItem[] {
    const metadata = this.selectedMetadata
    const items: IAuditDisplayItem[] = [
      { key: 'id', label: 'Audit event ID', value: this.selectedLog ? this.selectedLog.id : null, mono: true, copyable: true, max: 52 },
      { key: 'entityName', label: 'Entity name', value: metadata.entityName },
      { key: 'entityType', label: 'Entity type', value: this.prettyLabel(metadata.entityType) },
      { key: 'entityId', label: 'Entity ID', value: metadata.entityId, mono: true, copyable: true, max: 52 },
      { key: 'targetUser', label: 'Target user', value: metadata.targetUser },
      { key: 'requestMethod', label: 'Request method', value: metadata.requestMethod },
      { key: 'requestPath', label: 'Request path', value: metadata.requestPath, mono: true, copyable: true, max: 64 },
      { key: 'ipAddress', label: 'IP address', value: metadata.ipAddress, mono: true, copyable: true },
      { key: 'sessionId', label: 'Session ID', value: metadata.sessionId, mono: true, copyable: true, max: 52 },
      { key: 'userAgent', label: 'User agent', value: metadata.userAgent, copyable: true, max: 80 }
    ]
    return items.filter(item => this.hasValue(item.value))
  }

  get additionalDataItems (): IAuditDisplayItem[] {
    const additionalData = this.selectedMetadata.additionalData || {}
    return Object.keys(additionalData)
      .map(key => ({
        key,
        label: this.prettyLabel(key),
        value: additionalData[key],
        copyable: true,
        max: 90
      }))
      .filter(item => this.hasValue(item.value))
  }

  private async applyFilters () {
    this.localFilters.offset = 0
    await this.loadLogs(this.localFilters)
  }

  private async resetFilters () {
    this.localFilters = {
      from: '',
      to: '',
      actorUser: '',
      profile: '',
      action: '',
      limit: 50,
      offset: 0
    }
    await this.loadLogs(this.localFilters)
  }

  private async nextPage () {
    if (!this.canGoNext) {
      return
    }
    this.localFilters.offset = Number(this.localFilters.offset || 0) + Number(this.localFilters.limit || 50)
    await this.loadLogs(this.localFilters)
  }

  private async previousPage () {
    if (!this.canGoPrevious) {
      return
    }
    this.localFilters.offset = Math.max(0, Number(this.localFilters.offset || 0) - Number(this.localFilters.limit || 50))
    await this.loadLogs(this.localFilters)
  }

  private async loadLogs (filters: IAuditTrailLocalFilters) {
    if (!this.canView) {
      this.showSnackBar('warning', this.localText('settings.permissionDenied'))
      return
    }
    try {
      await this.$store.dispatch('auditTrail/loadAuditTrail', this.toAuditTrailFilters(filters))
      this.localFilters = this.fromAuditTrailFilters(this.auditTrailFilters)
    } catch (error) {
      this.showSnackBar('error', this.localText('auditTrail.loadFailed'))
    }
  }

  private async loadUserProfiles () {
    if (this.allUserProfiles && this.allUserProfiles.length > 0) {
      return
    }
    try {
      await this.$store.dispatch('user/getAllUserProfiles')
    } catch (error) {
      // The audit trail is still usable with built-in profile names if the user list cannot be loaded.
    }
  }

  private async loadRetentionPolicy () {
    try {
      const policy = await this.$store.dispatch('auditTrail/loadAuditRetentionPolicy')
      this.localRetentionPolicy = this.cloneRetentionPolicy(policy)
    } catch (error) {
      this.showSnackBar('error', this.localText('auditTrail.retentionLoadFailed'))
      this.localRetentionPolicy = this.cloneRetentionPolicy(this.auditRetentionPolicy)
    }
  }

  private async saveRetentionPolicy () {
    if (!this.canManageRetention) {
      this.showSnackBar('warning', this.localText('settings.permissionDenied'))
      return
    }
    const policy = this.retentionPolicyForSave()
    if (!policy) {
      this.showSnackBar('warning', this.localText('auditTrail.retentionInvalid'))
      return
    }
    try {
      const updatedPolicy = await this.$store.dispatch('auditTrail/updateAuditRetentionPolicy', policy)
      this.localRetentionPolicy = this.cloneRetentionPolicy(updatedPolicy)
      this.showSnackBar('success', this.localText('auditTrail.retentionSaved'))
    } catch (error) {
      this.showSnackBar('error', this.localText('auditTrail.retentionSaveFailed'))
    }
  }

  private async exportLogs (format: string) {
    if (!this.canExport) {
      this.showSnackBar('warning', this.localText('settings.permissionDenied'))
      return
    }
    try {
      const response = await this.$store.dispatch('auditTrail/exportAuditTrail', {
        format,
        filters: this.toAuditTrailFilters(this.localFilters)
      })
      const contentType = format === 'json' ? 'application/json' : 'text/csv'
      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: contentType })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = this.buildDownloadName(response.headers, format)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(link.href)
    } catch (error) {
      this.showSnackBar('error', this.localText('auditTrail.exportFailed'))
    }
  }

  private exportSelectedLog (format: string) {
    if (!this.canExport) {
      this.showSnackBar('warning', this.localText('settings.permissionDenied'))
      return
    }
    if (!this.selectedLog) {
      return
    }
    const normalizedFormat = format === 'json' ? 'json' : 'csv'
    const content = normalizedFormat === 'json'
      ? JSON.stringify(this.selectedLog, null, 2)
      : this.singleEntryCsv(this.selectedLog)
    const contentType = normalizedFormat === 'json' ? 'application/json' : 'text/csv'
    const timestamp = this.selectedLog.timestamp
      ? this.selectedLog.timestamp.replace(/:/g, '-')
      : moment().toISOString().replace(/:/g, '-')
    const blob = new Blob([content], { type: contentType })
    this.downloadBlob(blob, 'audit_trail_' + timestamp + '.' + normalizedFormat)
  }

  private buildDownloadName (headers: any, format: string): string {
    const disposition = headers && (headers['content-disposition'] || headers['Content-Disposition'])
    if (disposition) {
      const match = disposition.match(/filename="?([^"]+)"?/)
      if (match && match[1]) {
        return match[1]
      }
    }
    return 'audit_trail.' + format
  }

  private singleEntryCsv (entry: IAuditTrailEntry): string {
    return 'timestamp,username,actionType,description,metadata\n' + [
      this.csvValue(entry.timestamp || ''),
      this.csvValue(entry.username || ''),
      this.csvValue(entry.actionType || ''),
      this.csvValue(entry.description || ''),
      this.csvValue(JSON.stringify(entry.metadata || {}))
    ].join(',') + '\n'
  }

  private csvValue (value: string): string {
    return '"' + String(value).replace(/"/g, '""') + '"'
  }

  private downloadBlob (blob: Blob, fileName: string) {
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(link.href)
  }

  private openDetails (entry: IAuditTrailEntry) {
    this.selectedLog = entry
    this.detailsDialog = true
  }

  private hasFieldChanges (entry: IAuditTrailEntry | null): boolean {
    return !!(entry && entry.metadata && entry.metadata.fieldChanges && entry.metadata.fieldChanges.length > 0)
  }

  private formatTimestamp (timestamp?: string): string {
    return timestamp ? moment(timestamp).format('YYYY-MM-DD HH:mm:ss Z') : '-'
  }

  private formatAction (action?: string): string {
    return action ? action.replace(/_/g, ' ') : '-'
  }

  private actionClass (action?: string): string {
    const value = action || ''
    if (value.indexOf('LOGIN') >= 0 || value.indexOf('LOGOUT') >= 0 || value.indexOf('USER') >= 0) {
      return 'audit-action-user'
    }
    if (value.indexOf('AUDIT_TRAIL') >= 0) {
      return 'audit-action-audit'
    }
    if (value.indexOf('PALLET_PATTERN') >= 0 || value.indexOf('PATTERN') >= 0) {
      return 'audit-action-pattern'
    }
    if (value.indexOf('MACHINE_SETTINGS') >= 0 || value.indexOf('SETTINGS') >= 0) {
      return 'audit-action-settings'
    }
    if (value.indexOf('BACKUP') >= 0 || value.indexOf('RESTORE') >= 0) {
      return 'audit-action-backup'
    }
    if (value.indexOf('LICENSE') >= 0) {
      return 'audit-action-license'
    }
    return 'audit-action-default'
  }

  private entityLabel (metadata?: IAuditMetadata): string {
    if (!metadata) {
      return '-'
    }
    if (this.hasValue(metadata.entityName)) {
      return this.compactValue(metadata.entityName, 42)
    }
    if (this.hasValue(metadata.targetUser)) {
      return this.compactValue(metadata.targetUser, 42)
    }
    if (this.hasValue(metadata.entityType)) {
      return this.prettyLabel(metadata.entityType)
    }
    if (this.hasValue(metadata.entityId)) {
      return this.compactValue(metadata.entityId, 42)
    }
    return '-'
  }

  private entitySecondaryLabel (metadata?: IAuditMetadata): string {
    if (!metadata) {
      return ''
    }
    const values = []
    if (this.hasValue(metadata.entityType) && this.entityLabel(metadata) !== this.prettyLabel(metadata.entityType)) {
      values.push(this.prettyLabel(metadata.entityType))
    }
    if (this.hasValue(metadata.entityId) && this.entityLabel(metadata) !== this.compactValue(metadata.entityId, 42)) {
      values.push('ID ' + this.compactValue(metadata.entityId, 32))
    }
    return values.join(' / ')
  }

  private entityTitle (metadata?: IAuditMetadata): string {
    if (!metadata) {
      return '-'
    }
    const values = [
      metadata.entityName,
      metadata.entityType ? this.prettyLabel(metadata.entityType) : null,
      metadata.entityId,
      metadata.targetUser
    ].filter(value => this.hasValue(value))
    return values.length > 0 ? values.map(value => this.fullValue(value)).join(' / ') : '-'
  }

  private prettyLabel (value?: string): string {
    if (!value) {
      return '-'
    }
    const specialLabels: {[key: string]: string} = {
      id: 'ID',
      entityId: 'Entity ID',
      ipAddress: 'IP address',
      requestPath: 'Request path',
      requestMethod: 'Request method',
      sessionId: 'Session ID',
      targetUser: 'Target user',
      userAgent: 'User agent'
    }
    if (specialLabels[value]) {
      return specialLabels[value]
    }
    return value
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[-_.]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, letter => letter.toUpperCase())
  }

  private addUserFilterItem (target: {[key: string]: IAuditUserFilterItem}, value?: string, label?: string) {
    if (!value) {
      return
    }
    const trimmedValue = value.trim()
    if (!trimmedValue || target[trimmedValue]) {
      return
    }
    const text = label && label !== trimmedValue
      ? label + ' (' + trimmedValue + ')'
      : trimmedValue
    target[trimmedValue] = { text, value: trimmedValue }
  }

  private userDisplayName (userName: string): string {
    const profile = this.userProfilesList.find(item => item.id === userName)
    return profile ? profile.name : userName
  }

  private isDistinctActorUser (entry?: IAuditTrailEntry): boolean {
    const normalizedUsername = this.normalizedActorUser(entry)
    if (!normalizedUsername) {
      return false
    }
    if (normalizedUsername === 'system') {
      return false
    }
    const normalizedUserProfile = this.normalizedUserProfile(entry)
    if (normalizedUserProfile) {
      return normalizedUsername !== normalizedUserProfile
    }
    return !this.knownUserProfiles().includes(normalizedUsername)
  }

  private knownUserProfiles (): string[] {
    const profiles = new Set<string>()
    this.userProfilesList.forEach(profile => {
      if (profile && profile.id) {
        profiles.add(profile.id)
      }
    })
    const allProfiles = this.allUserProfiles || []
    allProfiles.forEach(profile => {
      if (profile && profile.profile) {
        profiles.add(profile.profile)
      }
    })
    return Array.from(profiles)
  }

  private normalizedActorUser (entry?: IAuditTrailEntry): string {
    if (!entry || !this.hasValue(entry.username)) {
      return ''
    }
    return entry.username!.trim()
  }

  private normalizedUserProfile (entry?: IAuditTrailEntry): string {
    if (!entry || !this.hasValue(entry.userProfile)) {
      return ''
    }
    return entry.userProfile!.trim()
  }

  private compactValue (value: any, maxLength: number = 54): string {
    const full = this.fullValue(value).replace(/\s+/g, ' ').trim()
    if (!full || full === '-') {
      return '-'
    }
    if (full.length <= maxLength) {
      return full
    }
    const availableLength = Math.max(8, maxLength - 3)
    const headLength = Math.ceil(availableLength * 0.6)
    const tailLength = availableLength - headLength
    return full.slice(0, headLength) + '...' + full.slice(full.length - tailLength)
  }

  private fullValue (value: any): string {
    if (value == null) {
      return '-'
    }
    if (typeof value === 'string') {
      return value
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value)
    }
    return JSON.stringify(value, null, 2)
  }

  private hasValue (value: any): boolean {
    if (value == null) {
      return false
    }
    return this.fullValue(value).trim() !== '' && this.fullValue(value) !== '-'
  }

  private async copyValue (value: any) {
    const text = this.fullValue(value)
    if (!this.hasValue(text)) {
      return
    }
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text)
      } else {
        this.copyWithTextArea(text)
      }
      this.showSnackBar('success', 'Copied to clipboard')
    } catch (error) {
      this.showSnackBar('error', 'Failed to copy value')
    }
  }

  private copyWithTextArea (text: string) {
    const element = document.createElement('textarea')
    element.value = text
    element.setAttribute('readonly', 'true')
    element.style.position = 'absolute'
    element.style.left = '-9999px'
    document.body.appendChild(element)
    element.select()
    document.execCommand('copy')
    document.body.removeChild(element)
  }

  private fromAuditTrailFilters (filters?: IAuditTrailFilters): IAuditTrailLocalFilters {
    const source = filters || {}
    return {
      from: source.from || '',
      to: source.to || '',
      actorUser: source.user || '',
      profile: source.profile || '',
      action: source.action || '',
      limit: source.limit != null ? source.limit : 50,
      offset: source.offset != null ? source.offset : 0
    }
  }

  private toAuditTrailFilters (filters?: IAuditTrailLocalFilters): IAuditTrailFilters {
    const source = filters || {} as IAuditTrailLocalFilters
    return {
      from: source.from || '',
      to: source.to || '',
      user: source.actorUser || '',
      profile: source.profile || '',
      action: source.action || '',
      limit: source.limit != null ? source.limit : 50,
      offset: source.offset != null ? source.offset : 0
    }
  }

  private defaultRetentionPolicy (): IAuditRetentionPolicy {
    return {
      enabled: true,
      retentionDays: 365,
      cleanupBatchSize: 10000,
      cleanupIntervalHours: 24
    }
  }

  private cloneRetentionPolicy (policy?: IAuditRetentionPolicy | null): IAuditRetentionPolicy {
    const source = policy || this.defaultRetentionPolicy()
    const enabled = source.enabled != null ? source.enabled : source.retentionDays !== 0
    return {
      enabled,
      retentionDays: source.retentionDays != null ? source.retentionDays : (enabled ? 365 : 0),
      cleanupBatchSize: source.cleanupBatchSize != null ? source.cleanupBatchSize : 10000,
      cleanupIntervalHours: source.cleanupIntervalHours != null ? source.cleanupIntervalHours : 24
    }
  }

  private retentionPolicyForSave (): IAuditRetentionPolicy | null {
    const enabled = !!this.localRetentionPolicy.enabled
    const retentionDays = enabled ? Number(this.localRetentionPolicy.retentionDays) : 0
    const cleanupBatchSize = Number(this.localRetentionPolicy.cleanupBatchSize)
    const cleanupIntervalHours = Number(this.localRetentionPolicy.cleanupIntervalHours)
    if (enabled && !this.isIntegerInRange(retentionDays, 1, 36500)) {
      return null
    }
    if (!this.isIntegerInRange(cleanupBatchSize, 1, 100000)) {
      return null
    }
    if (!this.isIntegerInRange(cleanupIntervalHours, 1, 168)) {
      return null
    }
    return {
      enabled,
      retentionDays,
      cleanupBatchSize,
      cleanupIntervalHours
    }
  }

  private isIntegerInRange (value: number, min: number, max: number): boolean {
    return Number.isInteger(value) && value >= min && value <= max
  }

  private localText (key: string, values?: string[]): string {
    return String(this.$t(key, values))
  }

  private showSnackBar (color: string, text: string) {
    this.snackbar.show = true
    this.snackbar.color = color
    this.snackbar.text = text
  }
}
